import moment from "moment";
import { REVIEW_REQUEST_SECTIONS } from "../../../../constants/reviewRequest";
import { getEducationQualificationLabel } from "../../../../utils/educationHelpers";

const SECTION_LABELS = REVIEW_REQUEST_SECTIONS.reduce((map, section) => {
  map[section.key] = section.label;
  return map;
}, {});

const formatWhen = (value, { compact = false } = {}) => {
  if (!value) return "";
  const parsed = moment(value);
  if (!parsed.isValid()) return String(value);
  return compact
    ? parsed.format("D MMM, h:mm a")
    : parsed.format("D MMM YYYY, h:mm a");
};

const latestEntry = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries[entries.length - 1];
};

export const getSectionLabel = (sectionKey) =>
  SECTION_LABELS[sectionKey] || sectionKey || "";

const formatHistoryValue = (fieldKey, value) => {
  const text = String(value ?? "").trim();
  if (!text || fieldKey !== "qualification") return text;
  return getEducationQualificationLabel(text);
};

/**
 * Peach "Edited by HR" tag shown next to an HR-edited field/document label.
 * Clickable when onClick is provided — fetches get_hr_edit_history.
 */
export const HrEditedByBadge = ({
  entries = [],
  onClick,
  expanded = false,
  visible = false,
}) => {
  if (!latestEntry(entries) && !visible) return null;

  if (typeof onClick !== "function") {
    return <span className="rr-hr-edited-badge">Edited by HR</span>;
  }

  return (
    <button
      type="button"
      className="rr-hr-edited-badge rr-hr-edited-badge--button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }}
      aria-expanded={!!expanded}
      aria-label={
        expanded ? "Hide HR edit history" : "Show HR edit history"
      }
    >
      Edited by HR
    </button>
  );
};

/**
 * Compact latest-edit line: when · who · what.
 */
export const HrEditedMeta = ({ entries = [] }) => {
  const entry = latestEntry(entries);
  if (!entry) return null;

  const when = formatWhen(entry.editedAt);
  const who = String(entry.editedBy || "").trim();
  const what = String(entry.label || entry.fieldKey || "").trim();
  const parts = [
    when ? `Edited on ${when}` : "",
    who,
    what,
  ].filter(Boolean);

  if (!parts.length) return null;
  return <div className="rr-hr-edited-meta">{parts.join(" • ")}</div>;
};

/**
 * Inline field-level history from get_hr_edit_history (screenshot panel).
 */
export const HrFieldApiHistoryPanel = ({ state, fieldKey = "" }) => {
  if (!state || state.status === "idle") return null;

  if (state.status === "loading") {
    return (
      <div className="rr-hr-api-history" aria-live="polite">
        <div className="rr-hr-api-history-status">Loading edit history…</div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="rr-hr-api-history rr-hr-api-history--error" aria-live="polite">
        <div className="rr-hr-api-history-status">
          {state.error || "Unable to load edit history"}
        </div>
      </div>
    );
  }

  const changes = Array.isArray(state.changes) ? state.changes : [];
  const candidateValue = formatHistoryValue(fieldKey, state.candidateValue);
  const hasContent = !!candidateValue || changes.length > 0;

  if (!hasContent) {
    return (
      <div className="rr-hr-api-history" aria-live="polite">
        <div className="rr-hr-api-history-status">No edit history available.</div>
      </div>
    );
  }

  return (
    <div className="rr-hr-api-history" aria-live="polite">
      {candidateValue ? (
        <div className="rr-hr-api-history-block">
          <div className="rr-hr-api-history-caption">Candidate entered</div>
          <div className="rr-hr-api-history-value">{candidateValue}</div>
        </div>
      ) : null}

      {changes.map((entry, index) => {
        const when = formatWhen(entry.changedAt, { compact: true });
        const who = String(entry.changedBy || "").trim();
        const changedLine = [
          who ? `Changed by ${who}` : "",
          when,
        ]
          .filter(Boolean)
          .join(" · ");
        const newValue = formatHistoryValue(fieldKey, entry.newValue);
        const reason = String(entry.reason || "").trim();

        if (!changedLine && !newValue && !reason) return null;

        return (
          <div
            key={entry.key || `change-${index}`}
            className="rr-hr-api-history-block"
          >
            {changedLine ? (
              <div className="rr-hr-api-history-caption">{changedLine}</div>
            ) : null}
            {newValue ? (
              <div className="rr-hr-api-history-value">{newValue}</div>
            ) : null}
            {reason ? (
              <div className="rr-hr-api-history-reason">Reason: {reason}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Right-side Edit history panel (Review summary).
 * Shows full audit cards for every confirmed HR edit/replacement in-session.
 */
const HrEditHistoryPanel = ({ entries = [] }) => {
  if (!entries.length) return null;

  const ordered = [...entries].slice().reverse();

  return (
    <div className="rr-hr-history">
      <div className="rr-hr-history-title">Edit history</div>
      <ol className="rr-hr-history-list">
        {ordered.map((entry, index) => {
          const oldValue = formatHistoryValue(entry.fieldKey, entry.oldValue);
          const newValue = formatHistoryValue(entry.fieldKey, entry.newValue);
          const sectionLabel = getSectionLabel(entry.sectionKey);
          const fieldLabel = entry.label || entry.fieldKey || "Field";

          return (
            <li
              key={`${entry.sectionKey}-${entry.fieldKey}-${entry.recordIndex}-${entry.editedAt}-${index}`}
              className="rr-hr-history-card"
            >
              <div className="rr-hr-history-card-header">
                <div className="rr-hr-history-label">{fieldLabel}</div>
                {sectionLabel ? (
                  <div className="rr-hr-history-section">{sectionLabel}</div>
                ) : null}
              </div>
              <div className="rr-hr-history-change">
                {oldValue ? (
                  <div className="rr-hr-history-old">{oldValue}</div>
                ) : null}
                <div className="rr-hr-history-new">{newValue || "—"}</div>
              </div>
              <div className="rr-hr-history-meta">
                {[entry.editedBy, formatWhen(entry.editedAt, { compact: true })]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
              {entry.reason ? (
                <div className="rr-hr-history-reason">
                  Reason: {entry.reason}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/** @deprecated Prefer HrEditedByBadge + HrEditedMeta for field chrome */
export const HrFieldHistory = ({ entries = [] }) => {
  if (!latestEntry(entries)) return null;
  return (
    <div className="rr-hr-field-history">
      <HrEditedByBadge entries={entries} />
      <HrEditedMeta entries={entries} />
    </div>
  );
};

export default HrEditHistoryPanel;
